import { IconName } from '@shared/components/core/images/IconMap';
import { CommonResourceKey } from '@shared/models/ResourceKeys';
import i18next from 'i18next';
import { FaceControlDescription, FaceControls } from './controls/FaceControls';
import { HeadControlDescription, HeadControls } from './controls/HeadControls';
import { ExternalKeyboardControlDescription } from './controls/KeyboardRemapControls';
import { TiltControlDescription, TiltControls } from './controls/TiltMotionControls';

export const ExpressionControls = [...FaceControls, ...HeadControls] as const;
export type ExpressionControlsType = (typeof ExpressionControls)[number];

export const IsExpressionCommand = (command: string) => ExpressionControls.indexOf(command) > -1;
export const IsTiltCommand = (command: string) => TiltControls.indexOf(command) > -1;
export const IsFaceCommand = (command: string) => FaceControls.indexOf(command) > -1;
export const IsHeadControl = (command: string) => HeadControls.indexOf(command) > -1;
export const IsHotkeyCommand = (command: string) => command?.startsWith('hotkey') == true;
export const IsMouseMoveCommand = (command: string) => command?.startsWith('mouse_move') == true;
export const IsJoystickMoveCommand = (command: string) => command?.startsWith('joystick_move') == true;
export const IsKeyboardRemapCommand = (command: string) => command?.startsWith('keyboard_') == true;
export const StopAllOutputsCommand = 'stop_all_outputs';
export const hotkeyPrefix = 'hotkey_';
export const mouseMovePrefix = 'mouse_move';
export const mouseMoveCommand = () => i18next.t('common:event.mouseMove.commandLabel');
export const mouseMoveStartCommand = () => i18next.t('common:event.mouseMove.startCommand');
export const mouseMoveStopCommand = () => i18next.t('common:event.mouseMove.stopCommand');

export type CommandType =
    | 'expression'
    | 'external_keyboard'
    | 'head'
    | 'hotkey'
    | 'motion'
    | 'spoken'
    | 'tilt'
    | 'voice'
    | 'dictation'
    | 'smartTasks'
    | 'other';

export const GetCommandType = (command: string): CommandType => {
    if (IsFaceCommand(command)) {
        return 'expression';
    }
    if (IsHeadControl(command)) {
        return 'head';
    }
    if (IsTiltCommand(command)) {
        return 'tilt';
    }
    if (IsHotkeyCommand(command)) {
        return 'hotkey';
    }
    if (IsKeyboardRemapCommand(command)) {
        return 'external_keyboard';
    }
    if (command.includes('_')) {
        return 'other';
    }
    return 'voice';
};
export type OnboardingCommandType = 'voice' | 'expression' | 'head' | 'hotkey' | 'motion';
export const commandOptions: CommandType[] = ['voice', 'head', 'expression', 'hotkey', 'other'];
export const commandTypesDisplay: Record<
    CommandType,
    {
        title: CommonResourceKey;
        description: CommonResourceKey;
        icon: IconName;
        alt: string;
        question: CommonResourceKey;
    }
> = {
    hotkey: {
        title: 'common:hotkey.titlePanel',
        description: 'common:hotkey.description',
        question: 'common:hotkey.question',
        icon: 'virtualButton',
        alt: 'Virtual Buttons',
    },
    //TODO: we need to double check this spoken was used for voice controls in some places but not others
    spoken: {
        title: 'common:talkback.title',
        description: 'common:talkback.description',
        question: 'common:talkback.question',
        icon: 'speaker',
        alt: 'Talkback',
    },
    voice: {
        title: 'common:voice.titlePlural',
        description: 'common:voice.description',
        question: 'common:voice.question',
        icon: 'voiceControl',
        alt: 'Voice Control',
    },
    expression: {
        title: 'common:expression.titlePlural',
        description: 'common:expression.description',
        question: 'common:face.question',
        icon: 'faceExpression',
        alt: 'Facial Expression Control',
    },
    head: {
        title: 'common:head.titlePlural',
        description: 'common:head.description',
        question: 'common:head.question',
        icon: 'headMovement',
        alt: 'Head Movement Control',
    },
    tilt: {
        title: 'common:motion.titlePlural',
        description: 'common:motion.description',
        question: 'common:motion.question',
        icon: 'motionControl',
        alt: 'Motion Control',
    },
    //TODO: We need to double check this - I believe this is Remap Keyboard and not Virtual Buttons (hotkeys)
    external_keyboard: {
        title: 'common:keys.titlePlural',
        description: 'common:keys.description',
        question: 'common:keys.question',
        icon: 'virtualButton',
        alt: 'Keys & Buttons',
    },
    //TODO: We need to double check this - I believe this is a dupe of Tilt
    motion: {
        title: 'common:motion.titlePlural',
        description: 'common:motion.description',
        question: 'common:motion.question',
        icon: 'motionControl',
        alt: 'Motion Control',
    },
    dictation: {
        title: 'common:dictation.title',
        description: 'common:dictation.description',
        question: 'common:dictation.question',
        icon: 'dictationPlay',
        alt: 'Dictation Control',
    },
    //TODO: Practically speaking, this is the same as voice but it is treated distinctly elsewhere. We might need to change how we assign voice and other -- e.g. check for underscore and not associated with other commandtypes
    other: {
        title: 'common:other.title',
        description: 'common:other.description',
        question: 'common:other.question',
        icon: 'voiceControl',
        alt: 'Other Control',
    },
    smartTasks: {
        title: 'common:smartTasks.title',
        description: 'common:smartTasks.description',
        question: 'common:smartTasks.title',
        icon: 'ai',
        alt: 'Smart Tasks Control',
    },
};

export const tiltControls = TiltControlDescription;
export const expressionCommands = [...HeadControlDescription, ...FaceControlDescription];
export const externalKeyboardCommands = ExternalKeyboardControlDescription;
export const headCommands = HeadControlDescription;
export const faceCommands = FaceControlDescription;
export const allControls = [...tiltControls, ...expressionCommands, ...externalKeyboardCommands];

export const getCommandDisplayIcon = (command: string): IconName | null => {
    if (!command) return null;
    if (command.startsWith('mouse_move')) return 'mouse';
    if (faceCommands.find((c) => c.value == command)) return 'faceExpression';
    if (headCommands.find((c) => c.value == command)) return 'headMovement';
    if (tiltControls.find((c) => c.value == command)) return 'motionControl';
    if (externalKeyboardCommands.find((c) => c.value == command)) return 'keyboard';
    if (command.startsWith(hotkeyPrefix)) return 'virtualButton';
    return 'voiceControl';
};
